// Builds the payload string for a Raast P2P (IBAN-based) receive QR.
//
// SHARED WITH THE CLIENT BUNDLE -- this file must stay parseable as
// CommonJS *after Babel has finished with it*, which is a stricter rule
// than "don't write import/export". CRA compiles everything under
// client/src, and when Babel down-levels certain syntax it injects its
// own helper `import` statement at the top of the output. That injected
// import is ESM syntax, so webpack then classifies the compiled module
// as an ES module, which makes `module.exports` read-only: the
// assignment at the bottom of this file throws at load time and takes
// the whole bundle down with it, while a named import of it fails to
// resolve at build time.
//
// The trigger here was `for...of`, which pulls in
// _createForOfIteratorHelper. So: indexed `for` loops only, no `for...of`,
// no iterable spread, no async/generators in this file or any other
// CommonJS module under client/src (socialPlatformRegistry.js has the
// same constraint and happens to satisfy it by never having used any).
// test/sharedCjsModules.test.js enforces this by compiling each such
// module and asserting Babel emitted no import.
//
// The structure below is NOT taken from the SBP published standard, and
// deliberately so. That document (sbp.org.pk/disd/2022/CL2-Annex.pdf) is
// not retrievable, and the only third-party implementations that claim
// to follow it build the much larger EMVCo *merchant-presented* (P2M)
// payload -- currency 586, merchant category, merchant name, city, a
// nested tag 26 carrying a "pk.gov.sbp" GUID, CRC at tag 63. That is a
// different format from the one Pakistani banking apps actually emit for
// a personal receive QR.
//
// This implements the real P2P payload, decoded from a QR generated by
// HBL Mobile and confirmed interoperable by scanning it in NayaPay -- a
// different provider -- where it opened pre-filled account details and
// prompted for an amount. Interoperability across two unrelated
// providers is what identifies this as the SBP-standardised P2P format
// rather than an HBL-proprietary one.
//
//   tag 00  len 02  "02"        payload format / version
//   tag 01  len 02  "11"        static QR (no embedded amount)
//   tag 02  len 02  "00"        reserved
//   tag 04  len 24  <IBAN>      PK + 2 check digits + 4 bank code + 16
//   tag 10  len 04  <CRC>       CRC-16/CCITT-FALSE
//
// The CRC is computed over the entire payload INCLUDING the trailing
// "1004" tag-and-length prefix, and excluding only the four checksum
// characters themselves. This is the same convention EMVCo uses for its
// own tag 63, and getting it wrong is the single most likely way to
// produce a QR that looks right and silently fails to scan.
//
// Total payload is always exactly 54 characters, since every field is
// fixed width.

// Fixed field values. Named rather than inlined because "02" appearing
// as both a tag and a value is otherwise genuinely confusing to read.
const PAYLOAD_FORMAT_VERSION = '02';
const POINT_OF_INITIATION_STATIC = '11';
const RESERVED_VALUE = '00';

const TAG_FORMAT = '00';
const TAG_POINT_OF_INITIATION = '01';
const TAG_RESERVED = '02';
const TAG_IBAN = '04';
const TAG_CRC = '10';

const IBAN_LENGTH = 24;
const CRC_LENGTH = 4;
const PAYLOAD_LENGTH = 54;

// ISO 13616 registers Pakistan as PK2!n4!a16!c -- two check digits, a
// four-letter bank code, then sixteen *alphanumeric* characters. Not
// sixteen digits: some banks issue account segments containing letters,
// and a digits-only rule would reject those IBANs outright. The mod-97
// check below is what actually catches typos, so this pattern only needs
// to be structurally correct, not defensively narrow.
const PK_IBAN_RE = /^PK\d{2}[A-Z]{4}[A-Z0-9]{16}$/;

// CRC-16/CCITT-FALSE: polynomial 0x1021, initial value 0xFFFF, no input
// or output reflection, no final XOR. Masked to 16 bits every iteration
// rather than only at the end -- without that, `crc << 1` climbs into
// the sign bit of JavaScript's 32-bit bitwise ints on a long input.
const crc16CcittFalse = (input) => {
  let crc = 0xffff;
  for (let i = 0; i < input.length; i += 1) {
    crc ^= (input.charCodeAt(i) & 0xff) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(CRC_LENGTH, '0');
};

// Two-digit decimal length, matching the payload's own encoding. Every
// field here is fixed width and well under 100, but a value that
// overflowed would produce a silently malformed payload rather than an
// error, so it throws instead.
const tlv = (tag, value) => {
  if (value.length > 99) {
    throw new Error(`Value for tag ${tag} is too long to encode (${value.length} chars)`);
  }
  return `${tag}${String(value.length).padStart(2, '0')}${value}`;
};

// Strips the spaces banks print IBANs with and normalises case. Applied
// before validation so a user pasting "PK36 SCBL 0000 0011 2345 6702"
// off a statement is accepted rather than told it is invalid.
const normalizeIban = (iban) => String(iban || '').replace(/[\s-]/g, '').toUpperCase();

// ISO 7064 mod-97-10: move the first four characters to the end, map
// letters to two-digit numbers (A=10 .. Z=35), and take the whole thing
// mod 97. A valid IBAN leaves remainder 1. Computed digit by digit
// because the intermediate value far exceeds Number.MAX_SAFE_INTEGER.
// Indexed loops, not for...of, and that is load-bearing -- see the
// "shared with the client bundle" note at the top of this file.
const ibanMod97 = (iban) => {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (let i = 0; i < rearranged.length; i += 1) {
    const code = rearranged.charCodeAt(i);
    // A-Z map to 10-35; digits pass through as themselves.
    const chunk = code >= 65 && code <= 90 ? String(code - 55) : rearranged[i];
    for (let j = 0; j < chunk.length; j += 1) {
      remainder = (remainder * 10 + Number(chunk[j])) % 97;
    }
  }
  return remainder;
};

// Returns null when valid, or a human-readable reason when not. Returning
// a reason rather than a boolean so the editor can tell the user which of
// the three failure modes they hit -- "that isn't 24 characters" and
// "those check digits don't match" need different corrections.
const validatePakistaniIban = (iban) => {
  const normalized = normalizeIban(iban);
  if (!normalized) return 'Enter an IBAN.';
  if (normalized.length !== IBAN_LENGTH) {
    return `A Pakistani IBAN is ${IBAN_LENGTH} characters; this one is ${normalized.length}.`;
  }
  if (!normalized.startsWith('PK')) return 'A Pakistani IBAN starts with PK.';
  if (!PK_IBAN_RE.test(normalized)) {
    return 'That does not look like a Pakistani IBAN (expected PK, 2 check digits, a 4-letter bank code, then 16 characters).';
  }
  if (ibanMod97(normalized) !== 1) {
    return 'The check digits do not match -- please re-check the IBAN.';
  }
  return null;
};

const isValidPakistaniIban = (iban) => validatePakistaniIban(iban) === null;

// Characters 5-8 of a Pakistani IBAN are the bank's own four-character
// code -- the same four that open its SWIFT/BIC (HABBPKKA -> HABB).
// That offset is a fact about IBAN structure, like every other offset in
// this file, so it is read here; what those four characters *mean* is
// display metadata and lives in client/src/utils/pakistaniBanks.js.
//
// Normalising first is the entire reason this is a function rather than
// a slice at the call site. Values are stored exactly as the user typed
// them -- route/api/users.js validates via validatePakistaniIban (which
// normalises internally) but never writes the normalised form back -- so
// a pasted "PK36 SCBL 0000 0011 2345 6702" is stored with its spaces and
// a raw slice(4, 8) would read "SCB " instead of "SCBL".
//
// Deliberately does NOT run the mod-97 check. The bank code is still
// readable from an IBAN whose account digits are mistyped, and refusing
// to name the bank in that case helps nobody -- the QR is already
// withheld from an invalid IBAN, which is the part that matters.
// Structurally non-IBAN input returns null rather than four characters
// of whatever was there, so callers fall back instead of captioning a
// payment detail with a guess.
const BANK_CODE_START = 4;
const BANK_CODE_END = 8;

const bankCodeFromIban = (iban) => {
  const normalized = normalizeIban(iban);
  if (!PK_IBAN_RE.test(normalized)) return null;
  return normalized.slice(BANK_CODE_START, BANK_CODE_END);
};

// Builds the full scannable payload. Throws on an invalid IBAN rather
// than emitting a payload, because a QR built from a bad IBAN is worse
// than no QR: it scans, shows a plausible screen, and sends money
// somewhere unintended.
const buildRaastP2PPayload = (iban) => {
  const normalized = normalizeIban(iban);
  const problem = validatePakistaniIban(normalized);
  if (problem) throw new Error(problem);

  const body =
    tlv(TAG_FORMAT, PAYLOAD_FORMAT_VERSION) +
    tlv(TAG_POINT_OF_INITIATION, POINT_OF_INITIATION_STATIC) +
    tlv(TAG_RESERVED, RESERVED_VALUE) +
    tlv(TAG_IBAN, normalized);

  // The CRC covers its own tag and length prefix but not its value.
  const upToCrcPrefix = `${body}${TAG_CRC}${String(CRC_LENGTH).padStart(2, '0')}`;
  return `${upToCrcPrefix}${crc16CcittFalse(upToCrcPrefix)}`;
};

// Inverse of the above, used by the tests and useful for verifying a QR
// decoded from a real banking app. Returns the parsed fields plus whether
// the embedded CRC matches a recomputation.
const parseRaastP2PPayload = (payload) => {
  const text = String(payload || '');
  const fields = {};
  let i = 0;

  while (i + 4 <= text.length) {
    const tag = text.slice(i, i + 2);
    const length = parseInt(text.slice(i + 2, i + 4), 10);
    if (Number.isNaN(length)) throw new Error(`Malformed length at offset ${i + 2}`);
    const value = text.slice(i + 4, i + 4 + length);
    if (value.length !== length) throw new Error(`Truncated value for tag ${tag}`);
    fields[tag] = value;
    i += 4 + length;
  }

  const crcStart = text.length - CRC_LENGTH;
  const expected = crc16CcittFalse(text.slice(0, crcStart));

  return {
    format: fields[TAG_FORMAT],
    pointOfInitiation: fields[TAG_POINT_OF_INITIATION],
    reserved: fields[TAG_RESERVED],
    iban: fields[TAG_IBAN],
    crc: fields[TAG_CRC],
    crcValid: fields[TAG_CRC] === expected,
  };
};

module.exports = {
  buildRaastP2PPayload,
  parseRaastP2PPayload,
  crc16CcittFalse,
  validatePakistaniIban,
  isValidPakistaniIban,
  bankCodeFromIban,
  normalizeIban,
  ibanMod97,
  PAYLOAD_LENGTH,
  IBAN_LENGTH,
};
